import { createSignal } from "solid-js";
import { createStore } from "solid-js/store";

import type { Rarity } from "./stickerStore";

const BASE = `${window.location.origin}/stickers`;

let tokenGetter: () => string | undefined = () => undefined;
/** Ligado na etapa 2B: informa como obter o token de sessao do usuario */
let tokenSet = false;
export const hasTokenGetter = () => tokenSet;
export function setTokenGetter(fn: () => string | undefined) {
  tokenGetter = fn;
  tokenSet = true;
}

/** Token de sessao do usuario (mesmas formas usadas pelo editor de stickers) */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function findToken(client: any, state?: any): string | undefined {
  const h = client?.authenticationHeader;
  if (Array.isArray(h) && typeof h[1] === "string" && h[0] === "x-session-token") return h[1];
  if (typeof client?.session?.token === "string") return client.session.token;
  const s = state?.auth?.getSession?.();
  return typeof s?.token === "string" ? s.token : undefined;
}
const token = () => {
  const t = tokenGetter();
  if (!t) throw new Error("sessão não encontrada");
  return t;
};

export type Card = {
  back?: string | null;
  id: string;
  name: string;
  url: string;
  rarity: Rarity;
  frame: string | null;
  supply: number | null;
  left: number | null;
};
export type OwnedCard = Card & { count: number; at: number; hasFrame: boolean };
export type FrameInfo = { id: string; url: string; rarity: Rarity };
export type FrameItem = FrameInfo & { name: string };
export type Pack = {
  id: string;
  name: string;
  url: string;
  dprice: number;
  count: number;
  limit: number | null;
  left: number | null;
  weights: Record<Rarity, number>;
  active: boolean;
  back: string | null;
  bg: string | null;
  cards: Card[];
};
export type PendingPack = { pid: string; at: number; pack: Pack };
export type OpenedCard = Card & {
  isNew: boolean;
  count: number;
  frameGained: boolean;
};

export async function api<T>(
  path: string,
  o: { method?: string; body?: unknown; auth?: boolean } = {},
): Promise<T> {
  const headers: Record<string, string> = {};
  if (o.body !== undefined) headers["content-type"] = "application/json";
  if (o.auth) headers["x-session-token"] = token();
  const r = await fetch(`${BASE}${path}`, {
    method: o.method ?? "GET",
    headers,
    body: o.body !== undefined ? JSON.stringify(o.body) : undefined,
    cache: "no-store",
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) {
    if (typeof d.diamonds === "number") setDiamonds(d.diamonds);
    throw new Error(d.error ?? `erro ${r.status}`);
  }
  return d as T;
}

/* ---------- diamantes ---------- */
const [diamonds, setDiamonds] = createSignal<number | null>(null);
export { diamonds };

export async function refreshDiamonds() {
  try {
    const d = await api<{ diamonds: number }>("/diamonds/me", { auth: true });
    setDiamonds(d.diamonds);
  } catch {
    /* sem sessao ou servico fora */
  }
}

export async function buyWithDiamonds(kind: "sticker" | "plate", id: string) {
  const d = await api<{ ok: boolean; diamonds: number }>("/buy", {
    method: "POST",
    auth: true,
    body: { kind, id },
  });
  setDiamonds(d.diamonds);
  return d;
}

/* ---------- pacotes ---------- */
export async function fetchPacks(): Promise<Pack[]> {
  const d = await api<{ packs: Pack[] }>("/packs/catalog");
  return d.packs ?? [];
}

export async function fetchPending(): Promise<{
  pending: PendingPack[];
  diamonds: number;
}> {
  const d = await api<{ pending: PendingPack[]; diamonds: number }>(
    "/packs/pending",
    { auth: true },
  );
  setDiamonds(d.diamonds);
  return d;
}

export async function buyPack(packId: string, qty = 1) {
  const d = await api<{ pid: string; pids?: string[]; diamonds: number }>(
    "/packs/buy",
    { method: "POST", auth: true, body: { packId, qty } },
  );
  setDiamonds(d.diamonds);
  return d;
}

export async function openPack(pid: string) {
  const d = await api<{ cards: OpenedCard[]; diamonds: number }>(
    "/packs/open",
    { method: "POST", auth: true, body: { pid } },
  );
  setDiamonds(d.diamonds);
  return d;
}

/* ---------- colecao e molduras ---------- */
export async function fetchOwnedCards(userId: string) {
  return api<{ owned: OwnedCard[]; frames: FrameItem[]; frame: string | null; equipped?: string[]; slots?: number }>(
    `/cards/owned/${userId}`,
  );
}

const [frames, setFrames] = createStore<Record<string, FrameInfo | null>>({});
const askedF = new Set<string>();
let fq: string[] = [];
let ft: number | undefined;

async function flushFrames() {
  ft = undefined;
  const ids = fq.splice(0, 100);
  if (!ids.length) return;
  try {
    const r = await fetch(`${BASE}/cards/frames?ids=${ids.join(",")}`);
    const d = r.ok ? await r.json() : {};
    for (const id of ids) setFrames(id, d[id] ?? null);
  } catch {
    for (const id of ids) askedF.delete(id);
  }
  if (fq.length) ft = window.setTimeout(flushFrames, 30);
}

/** Moldura de carta equipada pelo usuario (reativo, busca em lote) */
export function cardFrameOf(userId?: string): FrameInfo | null {
  if (!userId) return null;
  if (!askedF.has(userId)) {
    askedF.add(userId);
    fq.push(userId);
    if (ft === undefined) ft = window.setTimeout(flushFrames, 30);
  }
  return frames[userId] ?? null;
}

export async function equipFrame(userId: string, id: string | null) {
  const d = await api<{ frame: FrameInfo | null }>("/cards/frame", {
    method: "PUT",
    auth: true,
    body: { id },
  });
  askedF.add(userId);
  setFrames(userId, d.frame && typeof d.frame === "object" ? d.frame : null);
  return d.frame;
}

/** Salva as cartas equipadas (ordem importa) */
export async function saveEquippedCards(ids: string[]) {
  return api<{ equipped: string[]; slots: number }>("/cards/equipped", {
    method: "PUT",
    auth: true,
    body: { ids },
  });
}
